<!-- Prompt Generator Modal Container -->
<div
    class="flex flex-col h-[min(90vh,600px)] w-[min(95vw,600px)] overflow-hidden bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800">

    <!-- Header -->
    <div
        class="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gradient-to-r from-violet-50/50 to-indigo-50/50 dark:from-gray-950 dark:to-gray-950">
        <div class="flex items-center gap-3">
            <div class="p-2 rounded-xl bg-violet-100 dark:bg-violet-900/30">
                <app-icon-v2 name="sparkles" [size]="20" class="text-violet-600 dark:text-violet-400" />
            </div>
            <div>
                <h3 class="text-lg font-bold text-gray-900 dark:text-white leading-tight">AI Prompt Generator</h3>
                <p class="text-xs text-gray-500 dark:text-gray-400">Crafting your agent's perfect persona</p>
            </div>
        </div>
        <button (click)="onClose()"
            class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 dark:text-gray-500 transition-colors">
            <app-icon-v2 name="x" [size]="20" />
        </button>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 overflow-y-auto p-8 relative flex flex-col items-center justify-center">

        <!-- State: Initializing -->
        @if (state() === 'initializing') {
        <div class="flex flex-col items-center gap-6 text-center step-fade-in">
            <div class="relative">
                <div
                    class="h-20 w-20 rounded-full border-4 border-violet-100 dark:border-violet-900/20  border-t-violet-600 animate-spin">
                </div>
                <app-icon-v2 name="brain" [size]="32"
                    class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-violet-600" />
            </div>
            <div class="space-y-2">
                <p class="text-base font-semibold text-gray-900 dark:text-white">Analyzing Product Info</p>
                <p class="text-sm text-gray-500 dark:text-gray-400">Determining which details we still need from you...
                </p>
            </div>
        </div>
        }

        <!-- State: Questionnaire -->
        @if (state() === 'questionnaire') {
        <div class="w-full max-w-md flex flex-col gap-8 step-fade-in h-full justify-center">
            <div class="space-y-6">
                <div class="flex items-center gap-2 mb-2">
                    <span
                        class="px-2.5 py-1 rounded-full bg-violet-100 dark:bg-violet-900/40 text-[10px] font-bold text-violet-600 dark:text-violet-400 uppercase tracking-wider">
                        Step {{ stepCount() }} · Question {{ currentIndex() + 1 }}
                    </span>
                </div>

                <h4 class="text-xl font-bold text-gray-900 dark:text-white leading-snug">
                    {{ questions()[currentIndex()] }}
                </h4>

                <div class="relative group mt-8">
                    <textarea rows="4" [(ngModel)]="responses()[currentIndex()]"
                        placeholder="Type your response here..."
                        class="w-full bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 focus:border-violet-500 dark:focus:border-violet-500 rounded-2xl px-5 py-4 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-4 focus:ring-violet-500/10 transition-all resize-none shadow-sm"></textarea>
                </div>
            </div>

            <div class="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                <div class="flex items-center gap-3">
                    <app-button variant="ghost" size="md" [disabled]="currentIndex() === 0" (buttonClick)="onBack()">
                        Back
                    </app-button>
                </div>
                <div class="flex items-center gap-3">
                    <app-button variant="outline" size="md" (buttonClick)="onSkipQuestion()">
                        {{ currentIndex() === questions().length - 1 ? 'Skip & Finish' : 'Skip' }}
                    </app-button>
                    <app-button variant="default" size="md" (buttonClick)="onNext()">
                        {{ currentIndex() === questions().length - 1 ? 'Finish Step' : 'Next' }}
                    </app-button>
                </div>
            </div>
        </div>
        }

        <!-- State: Generating / Streaming -->
        @if (state() === 'generating' || state() === 'completed') {
        <div class="w-full h-full flex flex-col gap-6 step-fade-in">
            <div class="flex items-center justify-between">
                <div class="space-y-1">
                    <h4 class="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <app-icon-v2 [name]="state() === 'generating' ? 'loader-2' : 'check-circle-2'" [size]="18"
                            [class]="state() === 'generating' ? 'animate-spin text-violet-500' : 'text-emerald-500'" />
                        {{ state() === 'generating' ? 'Streaming System Prompt' : 'Draft Ready' }}
                    </h4>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Review the generated draft below before
                        applying.</p>
                </div>
            </div>

            <div class="flex-1 relative bg-gray-50 dark:bg-black/20 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div class="absolute top-4 right-4 flex items-center gap-2">
                     <span class="text-[10px] font-mono text-gray-400 uppercase tracking-widest">Persona Content</span>
                </div>
                <textarea 
                    readonly
                    [value]="displayPrompt()"
                    class="w-full h-full bg-transparent px-8 py-10 text-sm leading-relaxed text-gray-800 dark:text-gray-200 resize-none focus:outline-none scrollbar-theme whitespace-pre-wrap font-sans"
                ></textarea>
            </div>

            @if (state() === 'completed') {
            <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                <app-button variant="ghost" size="md" (buttonClick)="startGeneration()">
                    Regenerate
                </app-button>
                <app-button variant="default" size="md" [iconNameV2]="'check'" (buttonClick)="onApply()">
                    Apply Prompt
                </app-button>
            </div>
            }
        </div>
        }

        <!-- State: Error -->
        @if (state() === 'error') {
        <div class="flex flex-col items-center gap-6 text-center step-fade-in max-w-sm">
            <div class="p-4 rounded-full bg-red-100 dark:bg-red-500/20 text-red-600">
                <app-icon-v2 name="alert-triangle" [size]="48" />
            </div>
            <div class="space-y-2">
                <p class="text-lg font-bold text-gray-900 dark:text-white">Generation Failed</p>
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ errorMessage() }}</p>
            </div>
            <app-button variant="default" (buttonClick)="startGeneration()">Try Again</app-button>
        </div>
        }

    </div>
</div>